// Browser → /api/inspection/extract-stamp-gps wrapper. Reads a File as
// base64, POSTs with the user's Supabase JWT, returns parsed coords or null.
//
// Why null on failure (instead of throwing): the caller (useEvidenceGps) just
// wants to know whether stamp reading succeeded; the existing "missing GPS"
// UI path handles every failure mode (no overlay, low accuracy, outside
// Iran, network down, rate-limited). Throwing would force every caller to
// duplicate that try/catch.

import { supabase } from '../lib/supabaseClient';

/**
 * Read a File as base64 (without the `data:...;base64,` prefix).
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('FileReader returned non-string'));
        return;
      }
      // Strip "data:image/jpeg;base64," prefix.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

/**
 * Call the server-side Vision LLM stamp extractor for ONE photo.
 *
 * @param {File} file
 * @returns {Promise<null | {lat:number, lng:number, accuracy_m:number|null, note:string|null, model:string, source:'stamp'}>}
 */
export async function extractStampGps(file) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    if (!accessToken) return null;

    const image_base64 = await fileToBase64(file);
    const res = await fetch('/api/inspection/extract-stamp-gps', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ image_base64, mime_type: file.type || 'image/jpeg' }),
    });

    if (!res.ok) {
      // 422 = stamp not readable / outside Iran / low accuracy. 4xx/5xx of any
      // other kind also collapse to "couldn't extract" — caller treats them
      // the same as "no EXIF + no stamp".
      return null;
    }

    const body = await res.json();
    if (!body || typeof body.lat !== 'number' || typeof body.lng !== 'number') {
      return null;
    }
    return {
      lat: body.lat,
      lng: body.lng,
      accuracy_m: body.accuracy_m ?? null,
      note: body.note ?? null,
      model: body.model || null,
      source: 'stamp',
    };
  } catch {
    return null;
  }
}
